// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

// ** Components
import CloseIcon from '@/components/common/Lightbox/CloseIcon';
import CompetitionDetailMobile from './CompetitionDetailMobile';
import CompetitionDetailDesktop from './CompetitionDetailDesktop';
import { Icon } from '@/components/common/icons';
import { LightboxCloseButton, arrowSx, useLightboxNav } from '@/components/common/Lightbox';

// ** Types
import type { CompetitionCardModel, ImageEntry } from '@/components/common/CompetitionCard';

export type CompetitionDetailOverlayProps = {
	open: boolean;
	items: CompetitionCardModel[];
	activeIndex: number;
	onClose: () => void;
	onPrev: () => void;
	onNext: () => void;
};

const buildAllImages = (item: CompetitionCardModel): ImageEntry[] => {
	const images: ImageEntry[] = [];
	if (item.imageSrc) {
		images.push({ src: item.imageSrc, alt: item.imageAlt });
	}
	if (item.detail?.extraImages) {
		images.push(...item.detail.extraImages);
	}
	return images;
};

const mobileArrowSx = {
	color: 'common.black',
	p: 0,
	'&:disabled': { color: 'softGrey.main', opacity: 1 },
	'&:focusVisible': {
		outline: '2px solid',
		outlineColor: 'primary.main',
		outlineOffset: 2
	}
};

const CompetitionDetailOverlay = ({
	open,
	items,
	activeIndex,
	onClose,
	onPrev,
	onNext
}: CompetitionDetailOverlayProps) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const titleId = React.useId();
	const current = items[activeIndex];

	const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);

	const allImages = React.useMemo(() => (current ? buildAllImages(current) : []), [current]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: activeIndex is an intentional trigger to reset image selection
	React.useEffect(() => {
		setSelectedImageIndex(0);
	}, [activeIndex]);

	const handleSelectImage = React.useCallback((index: number) => {
		setSelectedImageIndex(index);
	}, []);

	const { hasPrev, hasNext } = useLightboxNav({
		open,
		itemCount: items.length,
		activeIndex,
		onClose,
		onPrev,
		onNext
	});

	if (!current) return null;

	/* ─── Mobile: full-screen white ─── */
	if (isMobile) {
		return (
			<Modal
				open={open}
				onClose={onClose}
				aria-labelledby={titleId}
				sx={{ outline: 'none', zIndex: 1500 }}
				slotProps={{
					backdrop: { sx: { bgcolor: 'common.white' } }
				}}
			>
				<Box
					sx={{
						position: 'fixed',
						inset: 0,
						display: 'flex',
						flexDirection: 'column',
						bgcolor: 'common.white',
						outline: 'none'
					}}
				>
					<Typography id={titleId} sx={visuallyHidden}>
						{current.name} — Detailansicht
					</Typography>

					<Typography aria-live="polite" aria-atomic="true" sx={visuallyHidden}>
						{`Beitrag ${activeIndex + 1} von ${items.length}: ${current.name}`}
					</Typography>

					{/* Mobile close button */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							px: 4,
							pt: 4,
							flexShrink: 0
						}}
					>
						<IconButton
							onClick={onClose}
							aria-label="Schließen"
							disableRipple
							sx={{
								color: 'common.black',
								'&:hover': { opacity: 0.6, bgcolor: 'transparent' },
								'&:focusVisible': {
									outline: '2px solid',
									outlineColor: 'primary.main',
									outlineOffset: 2
								}
							}}
						>
							<CloseIcon size={22} />
						</IconButton>
					</Box>

					{/* Scrollable content */}
					<Box sx={{ flex: 1, overflow: 'hidden' }}>
						<CompetitionDetailMobile
							item={current}
							allImages={allImages}
							selectedImageIndex={selectedImageIndex}
							onSelectImage={handleSelectImage}
						/>
					</Box>

					{/* Bottom entry navigation arrows */}
					{items.length > 1 && (
						<Box
							component="nav"
							aria-label="Beiträge durchblättern"
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								px: 6,
								py: 6,
								borderTop: 1,
								borderColor: 'divider',
								flexShrink: 0
							}}
						>
							<IconButton
								onClick={onPrev}
								disabled={!hasPrev}
								disableRipple
								aria-label="Vorheriger Beitrag"
								sx={mobileArrowSx}
							>
								<Icon name="arrowLeft" width={32} height={16} color="currentColor" />
							</IconButton>
							<IconButton
								onClick={onNext}
								disabled={!hasNext}
								disableRipple
								aria-label="Nächster Beitrag"
								sx={mobileArrowSx}
							>
								<Icon name="arrowRight" width={32} height={16} color="currentColor" />
							</IconButton>
						</Box>
					)}
				</Box>
			</Modal>
		);
	}

	/* ─── Desktop: dark backdrop, centered card ─── */
	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby={titleId}
			sx={{ outline: 'none', zIndex: 1500 }}
			slotProps={{
				backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.85)' } }
			}}
		>
			<Box
				sx={{
					position: 'fixed',
					inset: 0,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					outline: 'none',
					pt: 6,
					pb: 6
				}}
			>
				<Typography id={titleId} sx={visuallyHidden}>
					{current.name} — Detailansicht
				</Typography>

				<Typography aria-live="polite" aria-atomic="true" sx={visuallyHidden}>
					{`Beitrag ${activeIndex + 1} von ${items.length}: ${current.name}`}
				</Typography>

				{/* Desktop close button */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
						px: 20,
						mb: 4,
						flexShrink: 0
					}}
				>
					<Box sx={{ width: 80, display: 'flex', justifyContent: 'center' }}>
						<LightboxCloseButton onClick={onClose} />
					</Box>
				</Box>

				{/* Content area: arrows + card */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: { md: 3, lg: 6 },
						px: 20,
						flex: 1,
						minHeight: 0
					}}
				>
					{/* Prev arrow */}
					<Box
						sx={{
							flexShrink: 0,
							display: { xs: 'none', md: 'flex' },
							justifyContent: 'center'
						}}
					>
						{items.length > 1 && (
							<IconButton
								onClick={onPrev}
								disabled={!hasPrev}
								disableRipple
								aria-label="Vorheriger Beitrag"
								sx={arrowSx}
							>
								<Icon name="arrowLeft" width={32} height={16} color="white" opacity={hasPrev ? 1 : 0.3} />
							</IconButton>
						)}
					</Box>

					{/* Center — detail card */}
					<Box
						sx={{
							flex: 1,
							display: 'flex',
							justifyContent: 'center',
							minWidth: 0
						}}
					>
						<CompetitionDetailDesktop
							item={current}
							allImages={allImages}
							selectedImageIndex={selectedImageIndex}
							onSelectImage={handleSelectImage}
						/>
					</Box>

					{/* Next arrow */}
					<Box
						sx={{
							flexShrink: 0,
							display: { xs: 'none', md: 'flex' },
							justifyContent: 'center'
						}}
					>
						{items.length > 1 && (
							<IconButton onClick={onNext} disabled={!hasNext} disableRipple aria-label="Nächster Beitrag" sx={arrowSx}>
								<Icon name="arrowRight" width={32} height={16} color="white" opacity={hasNext ? 1 : 0.3} />
							</IconButton>
						)}
					</Box>
				</Box>
			</Box>
		</Modal>
	);
};

export default CompetitionDetailOverlay;
